import React, { useState, useEffect } from 'react';
import { Button, TextField, Checkbox, FormControlLabel, FormGroup, Box, Typography, ListItemText, Autocomplete, Chip, CircularProgress } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import { styled } from '@mui/material/styles';
import Grid from '@mui/system/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import FormHelperText from '@mui/material/FormHelperText';
import axios from 'axios';
import { toast } from 'react-toastify';

const EditItemForm = () => {
  const { id } = useParams(); 
  const [options, setOptions] = useState([]);   
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    discount: '',
    category: '',
    brand: '',
    quantityPack: '',
    itemQuantity: '',
    unit: '',
    hsnCode: '',
    description: '',
    itemattribute: [],
    publish: false,
    showBestOffers: false,
    showEstablishedBrands: false,
    image: null,
    shelfLife: '',
    country: '',
    fssaiLicense: '',
    expiryDate: '',
    packagingType: '',
    seller: '',
    sellerFssai: '',
    disclaimer: '',
  });

  const [cate, setCate] = useState([]);
  const [brandfetch, setBrandfetch] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inputValues, setInputValues] = useState({});
  const [selectedAttributes, setSelectedAttributes] = useState([]);

  // Fetch category and brand data when the component mounts
  useEffect(() => {
    const fetchCategory = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/categories/allcategories`);
        setCate(response.data);
      } catch (error) {
        console.error("Error fetching category data:", error);
      }
    };

    const fetchBrand = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/brands/allbrands`);
        setBrandfetch(response.data);
      } catch (error) {
        console.error("Error fetching brand data:", error);
      }
    };

    const fetchItemData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/items/itembyid/${id}`);
        const itemDetails = response.data?.product?.itemDetails;
        const generalInformation = response.data?.product?.generalInformation;
        setFormData({
          name: itemDetails.name,
          price: itemDetails.price,
          discount: itemDetails.discount,
          category: itemDetails.category?.name,
          brand: itemDetails.brand?.title,
          quantityPack: itemDetails.quantityPack,
          itemQuantity: itemDetails.itemQuantity,
          unit: itemDetails.unit,
          hsnCode: itemDetails.HSNCode,
          description: itemDetails.description,
          itemattribute: itemDetails.variants,
          publish: itemDetails.publish,
          showBestOffers: itemDetails.showInBestOffer,
          showEstablishedBrands: itemDetails.showInEstablishedBrands,
          image: itemDetails.mediaFiles ? itemDetails.mediaFiles[0].url : null,
          shelfLife: generalInformation.shelfLife,
          country: generalInformation.country,
          fssaiLicense: generalInformation.fssaiLicense,
          expiryDate: generalInformation.expiryDate,
          packagingType: generalInformation.packagingType,
          seller: generalInformation.seller,
          sellerFssai: generalInformation.sellerFSSAI,
          disclaimer: generalInformation.disclaimer,
        });

        setLoading(false); // Stop the loader after data is fetched
      } catch (error) {
        console.error("Error fetching item data:", error);
        setLoading(false); // Stop loader if error occurs
      }
    };
    const fetchItemAttribute = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/itemattribute/getall`);
        setOptions(response.data?.attributes.map(item => item.name));
      } catch (error) {
        console.error("Error fetching brand data:", error);
      }
    };
    fetchItemAttribute();
    fetchCategory();
    fetchBrand();
    fetchItemData();

  }, [id]);

  const handleChange = (e,newValue) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
      itemattribute: newValue,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setFormData({
      ...formData,
      image: file,
    });
  };

  const handleKeyDown = (attribute, event) => {
    if (event.key === "Enter" && event.target.value.trim() !== "") {
      setInputValues((prev) => ({
        ...prev,
        [attribute]: [...(prev[attribute] || []), event.target.value.trim()],
      }));
      event.target.value = "";
      event.preventDefault();
    }
  };  

  const handleDelete = (attribute, chipToDelete) => {
    setInputValues((prev) => ({
      ...prev,
      [attribute]: prev[attribute].filter((chip) => chip !== chipToDelete),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission

    const formDataToSend = new FormData();
    // Append all the form data as in the previous code
    // ...
    // Append form data for the item details
    formDataToSend.append('itemDetails[name]', formData.name);
    formDataToSend.append('itemDetails[price]', formData.price);
    formDataToSend.append('itemDetails[discount]', formData.discount);
    formDataToSend.append('itemDetails[categoryName]', formData.category);
    formDataToSend.append('itemDetails[brandName]', formData.brand);
    formDataToSend.append('itemDetails[quantityPack]', formData.quantityPack);
    formDataToSend.append('itemDetails[itemQuantity]', formData.itemQuantity);
    formDataToSend.append('itemDetails[unit]', formData.unit);
    formDataToSend.append('itemDetails[HSNCode]', formData.hsnCode);
    formDataToSend.append('itemDetails[description]', formData.description);
    formDataToSend.append('itemDetails[publish]', formData.publish);
    formDataToSend.append('itemDetails[showInBestOffer]', formData.showBestOffers);
    formDataToSend.append('itemDetails[showInEstablishedBrands]', formData.showEstablishedBrands);

    // Append general information
    formDataToSend.append('generalInformation[shelfLife]', formData.shelfLife);
    formDataToSend.append('generalInformation[country]', formData.country);
    formDataToSend.append('generalInformation[fssaiLicense]', formData.fssaiLicense);
    formDataToSend.append('generalInformation[expiryDate]', formData.expiryDate);
    formDataToSend.append('generalInformation[packagingType]', formData.packagingType);
    formDataToSend.append('generalInformation[seller]', formData.seller);
    formDataToSend.append('generalInformation[sellerFSSAI]', formData.sellerFssai);
    formDataToSend.append('generalInformation[disclaimer]', formData.disclaimer);
    // Handle image upload as in previous code
    if (formData.image) {
      formDataToSend.append('mediaFiles[]', formData.image);
    }

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/items/update/${id}`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.status === 200) {
        toast.success("Item updated successfully!");
        setFormData({
          name: '',
          price: '',
          discount: '',
          category: '',
          brand: '',
          quantityPack: '',
          itemQuantity: '',
          unit: '',
          hsnCode: '',
          description: '',
          itemattribute: [],
          publish: false,
          showBestOffers: false,
          showEstablishedBrands: false,
          image: null,
          shelfLife: '',
          country: '',
          fssaiLicense: '',
          expiryDate: '',
          packagingType: '',
          seller: '',
          sellerFssai: '',
          disclaimer: '',
        });
      }
    } catch (error) {
      toast.error("Error updating item. Please try again.");
      console.error("Error updating item:", error);
    }
  };

  return (
    <Box sx={{ maxWidth: '70%', margin: 'auto', padding: 2 }}>
      <form onSubmit={handleSubmit}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* The form fields go here as in your original code */}
            <fieldset
              style={{
                border: '1px solid #ccc',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px',
              }}
            >
              <legend
                style={{
                  padding: '8px',
                  width: 'auto',
                  background: '#3C7F10',
                  color: '#fff',
                  borderRadius: '8px',
                }}
              >
                Item Details
              </legend>
              <Box className='input-field-cls' sx={{ flexGrow: 1 }}>
                <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                  <Grid size={{ xs: 2, sm: 6 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      Name*
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      helperText="Insert name"
                    />
                  </Grid>
                  <Grid size={{ xs: 2, sm: 6 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      Price*
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      required
                      helperText="Insert Price"
                    />
                  </Grid>
                  <Grid size={{ xs: 2, sm: 6 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      Discount*
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      name="discount"
                      value={formData.discount}
                      onChange={handleChange}
                      required
                      helperText="Insert Discount (e.g., 5%, 10%, 30%)"
                    />
                  </Grid>
                  <Grid size={{ xs: 2, sm: 6 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      Category*
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        labelId="category-select-label"
                        id="category-select"
                        value={formData.category || ''}
                        onChange={handleChange}
                        name="category"
                        fullWidth
                      >
                        {cate?.categories?.map((category) => (
                          <MenuItem key={category.id} value={category.name}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 2, sm: 6 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      Brand*
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        labelId="brand-select-label"
                        id="brand-select"
                        value={formData.brand || ''}
                        onChange={handleChange}
                        name="brand"
                      >
                        {brandfetch?.brands?.map((brand) => (
                          <MenuItem key={brand.id} value={brand.title}>
                            {brand.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 2, sm: 6 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      Quantity Pack*
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      name="quantityPack"
                      value={formData.quantityPack}
                      onChange={handleChange}
                      required
                      helperText="Enter quantity pack (e.g. 45g, 1kg)"
                    />
                  </Grid>
                  <Grid size={{ xs: 2, sm: 6 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      Item Quantity*
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      name="itemQuantity"
                      value={formData.itemQuantity}
                      onChange={handleChange}
                      required
                      helperText="For unlimited, set to -1"
                    />
                  </Grid>
                  <Grid size={{ xs: 2, sm: 6 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      Unit*
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      name="unit"
                      value={formData.unit}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 2, sm: 6 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      HSN Code
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      name="hsnCode"
                      value={formData.hsnCode}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid size={{ xs: 2, sm: 12 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      Item Attribute*
                    </Typography>
                    <Autocomplete
                           multiple
                           id="item-attribute"
                           options={options}
                           value={selectedAttributes}
                           onChange={(event, newValue) => setSelectedAttributes(newValue)}
                           disableCloseOnSelect
                           renderInput={(params) => (
                             <TextField {...params} helperText="Select multiple attributes" />
                           )}
                           renderTags={(value, getTagProps) =>
                             value.map((option, index) => (
                               <Chip
                                 key={index}
                                 label={option}
                                 {...getTagProps({ index })}
                                 onDelete={() => {
                                   setSelectedAttributes(value.filter((item) => item !== option));
                                   setInputValues((prev) => {
                                     const updatedValues = { ...prev };
                                     delete updatedValues[option];
                                     return updatedValues;
                                   });
                                 }}
                               />
                             ))
                           }
                         />
                         {selectedAttributes.map((attribute) => (
                           <Box key={attribute} sx={{ marginTop: 2 }}>
                             <Typography>{attribute}</Typography>
                             <TextField
                             sx={{paddingLeft:0}}
                               variant="outlined"
                               fullWidth
                               onKeyDown={(e) => handleKeyDown(attribute, e)}
                               InputProps={{
                                 startAdornment: (
                                   <Box sx={{ display: "flex", flexWrap: "wrap"}}>
                                     {(inputValues[attribute] || []).map((chip, index) => (
                                       <Chip
                                         key={index}
                                         label={chip}
                                         onDelete={() => handleDelete(attribute, chip)}
                                       />
                                     ))}
                                   </Box>
                                 ),
                               }}
                             />
                           </Box>
                         ))}
                  </Grid>
                  <Grid size={{ xs: 2, sm: 12 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      Image
                    </Typography>

                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ marginBottom: '10px' }}
                    />
                    {formData.image && <Typography variant="body2">{formData.image.name}</Typography>}
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: '5px' }}>
                      Insert image
                    </Typography>
                    {formData.image && (
                      <Box>
                        <img
                          src={formData.image}
                          alt="Item"
                          style={{ width: '70px', height: '70px', marginTop: '10px', objectFit: 'cover' }} // Set width and height from backend data
                        />
                      </Box>
                    )}

                  </Grid>
                  <Grid size={{ xs: 2, sm: 12 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      Description*
                    </Typography>
                    <TextField
                      className='multiselect-cls'
                      variant="outlined"
                      fullWidth
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      multiline
                      rows={8}  // Set the number of rows for the textarea
                      required
                      helperText="Insert description"
                    />
                  </Grid>
                  <Grid size={{ xs: 2, sm: 12 }}>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.publish}
                            onChange={handleChange}
                            name="publish"
                          />
                        }
                        label="Publish"
                      />
                    </FormGroup>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.showBestOffers}
                            onChange={handleChange}
                            name="showBestOffers"
                          />
                        }
                        label="Show in Best Offers in Home Page"
                      />
                    </FormGroup>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.showEstablishedBrands}
                            onChange={handleChange}
                            name="showEstablishedBrands"
                          />
                        }
                        label="Show in Established Brands in Home Page"
                      />
                    </FormGroup>
                  </Grid>
                </Grid>
              </Box>


            </fieldset>
            <fieldset
              style={{
                border: '1px solid #ccc',
                padding: '20px',
                borderRadius: '8px',
                marginBottom: '20px',
              }}
            >
              <legend
                style={{
                  padding: '8px',
                  width: 'auto',
                  background: '#3C7F10',
                  color: '#fff',
                  borderRadius: '8px',
                }}
              >
                General Information
              </legend>
              <Box className="input-field-cls" sx={{ flexGrow: 1 }}>
                <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                  {/* Existing fields */}
                  <Grid size={{ xs: 2, sm: 6 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      Shelf Life*
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      name="shelfLife"
                      value={formData.shelfLife}
                      onChange={handleChange}
                      helperText="Insert Shelf Life"
                    />
                  </Grid>

                  {/* Country Field */}
                  <Grid size={{ xs: 2, sm: 6 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      Country*
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        name="country"
                        value={formData.country || ''}  // Preselect the country based on formData
                        onChange={handleChange}
                        id="country-dropdown"
                      >
                        <MenuItem value="India">India</MenuItem>
                        <MenuItem value="United States">United States</MenuItem>
                        <MenuItem value="Canada">Canada</MenuItem>
                        <MenuItem value="Canada">United Kingdom</MenuItem>
                        <MenuItem value="Canada">Australia</MenuItem>
                      </Select>
                      {formData.country === '' && <FormHelperText>Insert Country</FormHelperText>}
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 2, sm: 6 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      FSSAI License*
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      name="fssaiLicense"
                      value={formData.fssaiLicense}
                      onChange={handleChange}
                      helperText="Insert FSSAI License"
                    />
                  </Grid>

                  {/* Expiry Date Field */}
                  <Grid size={{ xs: 2, sm: 6 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      Expiry Date*
                    </Typography>

                    <TextField
                      variant="outlined"
                      fullWidth
                      name="expiryDate"
                      type="date"
                      value={formData.expiryDate || ''}
                      onChange={handleChange}
                      helperText="Insert Expiry Date"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 2, sm: 6 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      Packaging Type*
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      name="packagingType"
                      value={formData.packagingType}
                      onChange={handleChange}
                      helperText="Insert Packaging Type"
                    />
                  </Grid>

                  <Grid size={{ xs: 2, sm: 6 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      Seller*
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      name="seller"
                      value={formData.seller}
                      onChange={handleChange}
                      helperText="Insert Seller"
                    />
                  </Grid>

                  <Grid size={{ xs: 2, sm: 6 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      Seller FSSAI*
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      name="sellerFssai"
                      value={formData.sellerFssai}
                      onChange={handleChange}
                      helperText="Insert Seller FSSAI"
                    />
                  </Grid>

                  <Grid size={{ xs: 2, sm: 12 }}>
                    <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: '500' }}>
                      Disclaimer*
                    </Typography>
                    <TextField
                      variant="outlined"
                      fullWidth
                      name="disclaimer"
                      value={formData.disclaimer}
                      onChange={handleChange}
                      multiline
                      rows={8}
                      className='multiselect-cls'
                      helperText="Insert Disclaimer"
                    />
                  </Grid>

                </Grid>
              </Box>
            </fieldset>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button variant="contained" sx={{ bgcolor: 'green', color: '#fff' }} type="submit">
                <SaveIcon sx={{ pr: 1 }} />
                Save
              </Button>
              <Link to="/items" style={{ textDecoration: 'none' }}>
                <Button variant="contained" sx={{ bgcolor: 'transparent', color: 'green', boxShadow: 'unset' }}>
                  <SettingsBackupRestoreIcon sx={{ pr: 1 }} />
                  Back
                </Button>
              </Link>
            </Box>
          </>
        )}
      </form>

    </Box>
  );
};

export default EditItemForm;
